import { getAuthToken } from '../../services/auth/tokenStorage';
import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  TextInput,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp, useRoute, RouteProp } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../config';

import logo from '../../assets/logo.png';
import backIcon from '../../assets/icons/back.png';

import { createLogger } from '../../utils/logger';
const logger = createLogger('SecretCode');

interface SecretCodeScreenProps {
  navigation: NavigationProp<any>;
}

type RouteParams = {
  SecretCode: {
    requestId: string;
    primaryUserId: string;
  };
};

export const SecretCodeScreen: React.FC<SecretCodeScreenProps> = ({ navigation }) => {
  const route = useRoute<RouteProp<RouteParams, 'SecretCode'>>();
  const { requestId, primaryUserId } = route.params || {};

  const [secretCode, setSecretCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  // K27 (2026-10-22): a code lives two hours, and when the owner named no
  // code recipients it went straight to the successor with no way to get
  // another. The server sends a fresh one down the same route (recipients
  // first, the successor when none were named) and refuses a repeat
  // within ten minutes or while the request is locked.
  const handleResend = async () => {
    if (!requestId) {
      Alert.alert('Error', 'Request ID is missing');
      return;
    }
    try {
      setResending(true);
      const token = await getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_URL}/api/death-certificate/resend-secret-code`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ requestId }),
      });
      const result = await response.json();

      if (response.ok && result.success) {
        setSecretCode('');
        Alert.alert('New code sent', result.message);
      } else {
        const message = Array.isArray(result.message) ? result.message.join(' ') : result.message;
        Alert.alert('Could not send a new code', message || 'Please try again later.');
      }
    } catch (error) {
      logger.error('Error requesting a new secret code:', error);
      Alert.alert('Error', 'Could not send a new code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  // The one-time code is 8 digits (secret-code-delivery.service.ts). This
  // screen used to demand password-style complexity before calling the
  // server, so no successor could ever pass it (found 2026-09-28, K5 run).
  const validatePassword = (code: string): string | null => {
    if (!/^\d{8}$/.test(code.trim())) {
      return 'The code is the 8-digit number from the email or text message.';
    }
    return null;
  };

  const handleSubmit = async () => {
    // The server is the authority (3 tries, 15-minute lockout); this only stops obvious typos.
    const validationError = validatePassword(secretCode);
    if (validationError) {
      Alert.alert('Invalid Secret Code', validationError);
      return;
    }

    if (!requestId) {
      Alert.alert('Error', 'Request ID is missing');
      return;
    }

    try {
      setSubmitting(true);
      const token = await getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_URL}/api/death-certificate/validate-secret-code`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          requestId,
          secretCode: secretCode.trim(),
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        Alert.alert(
          'Success!',
          'Secret code validated successfully. You now have full access to this user\'s data.',
          [
            {
              text: 'OK',
              onPress: () => {
                // Navigate back to profile
                navigation.reset({
                  index: 0,
                  routes: [{ name: 'Profile' }],
                });
              }
            }
          ]
        );
      } else {
        Alert.alert('Error', result.message || 'Invalid secret code');
      }
    } catch (error) {
      logger.error('Error validating secret code:', error);
      Alert.alert('Error', 'Failed to validate secret code');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Image source={backIcon} style={styles.backIcon} />
        </TouchableOpacity>
        <Image source={logo} style={styles.logo} resizeMode="contain" />
      </View>

      <View style={styles.content}>
        <Text style={styles.title}>Enter Secret Code</Text>
        <Text style={styles.subtitle}>
          Enter the 8-digit code that was sent by email or text
        </Text>

        <View style={styles.formContainer}>
          <Text style={styles.label}>Secret Code</Text>
          <TextInput
            style={styles.input}
            placeholder="Enter secret code"
            placeholderTextColor="#999"
            value={secretCode}
            onChangeText={setSecretCode}
            secureTextEntry
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="number-pad"
            maxLength={8}
          />

          <View style={styles.requirementsContainer}>
            <Text style={styles.requirementsTitle}>About the code</Text>
            <Text style={styles.requirementText}>• 8 digits, valid for 2 hours from when it was sent</Text>
            <Text style={styles.requirementText}>• Sent to the recipients the owner chose, or to you when none were named</Text>
          </View>

          <TouchableOpacity
            style={[styles.submitButton, submitting && styles.submitButtonDisabled]}
            onPress={handleSubmit}
            disabled={submitting || !secretCode}
          >
            {submitting ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.submitButtonText}>Validate Secret Code</Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.resendButton, (resending || submitting) && styles.submitButtonDisabled]}
            onPress={handleResend}
            disabled={resending || submitting}
          >
            {resending ? (
              <ActivityIndicator color="#1C2C4F" />
            ) : (
              <Text style={styles.resendButtonText}>Send a new code</Text>
            )}
          </TouchableOpacity>
          <Text style={styles.resendHint}>
            Code expired or never arrived? A new one goes out the same way; the earlier code stops working.
          </Text>

          <View style={styles.warningContainer}>
            <Text style={styles.warningText}>
              ⚠️ You have 3 attempts to enter the correct secret code. After 3 failed attempts,
              you will be locked out for 15 minutes.
            </Text>
          </View>
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(10),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  logo: {
    width: s(100),
    height: s(100),
  },
  content: {
    flex: 1,
    padding: ms(20),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    textAlign: 'center',
    marginBottom: ms(10),
  },
  subtitle: {
    fontSize: ms(14, 0.3),
    color: '#666',
    textAlign: 'center',
    marginBottom: ms(30),
  },
  formContainer: {
    flex: 1,
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  input: {
    backgroundColor: '#FFF',
    borderRadius: ms(10),
    borderWidth: ms(1),
    borderColor: '#DDD',
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(20),
  },
  requirementsContainer: {
    backgroundColor: '#E8F4F8',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(20),
  },
  requirementsTitle: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  requirementText: {
    fontSize: ms(12, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(5),
  },
  submitButton: {
    backgroundColor: '#1C2C4F',
    borderRadius: ms(10),
    padding: ms(15),
    alignItems: 'center',
    marginBottom: ms(20),
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
  submitButtonText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  resendButton: {
    borderRadius: ms(10),
    borderWidth: ms(1),
    borderColor: '#1C2C4F',
    padding: ms(13),
    alignItems: 'center',
    marginBottom: ms(8),
  },
  resendButtonText: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  resendHint: {
    fontSize: ms(12, 0.3),
    color: '#666',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  warningContainer: {
    backgroundColor: '#FFF3CD',
    borderRadius: ms(10),
    padding: ms(15),
  },
  warningText: {
    fontSize: ms(13, 0.3),
    color: '#856404',
    textAlign: 'center',
  },
});
